import { Injectable } from '@angular/core';
import { CallLog, CallLogTab, Contact, CursorPaging, Department, Extension, Followers, Hotline, Relationship, END_REASON } from '@etop/models';
import { CallLogStore } from '@etop/state/shop/call-log/call-log.store';
import { CallLogQuery } from '@etop/state/shop/call-log/call-log.query';
import { ExtensionQuery } from '@etop/state/shop/extension';
import { HotlineQuery } from '@etop/state/shop/hotline';
import { ContactQuery, ContactService } from '@etop/state/shop/contact';
import { RelationshipQuery, RelationshipService } from '@etop/state/relationship';
import { CallLogAPI, CallLogApi } from "@etop/api/shop/call-log.api";
import { ContactAPI } from "@etop/api/shop/contact.api";
import { AuthorizationAPI } from '@etop/api';
import { DepartmentQuery } from '../department';
import { ZaloAPI } from '@etop/api/shop/zalo.api';
import { OAFollowersQuery, OAFollowersService } from '@etop/state/zalo/oa-followers';
import { OAConnectionService } from '@etop/state/zalo/oa-connection';

@Injectable({
  providedIn: 'root'
})
export class CallLogService {
  constructor(
    private callLogApi: CallLogApi,
    private callLogStore: CallLogStore,
    private callLogQuery: CallLogQuery,
    private extensionQuery: ExtensionQuery,
    private contactService: ContactService,
    private contactQuery: ContactQuery,
    private hotlineQuery: HotlineQuery,
    private relationshipQuery: RelationshipQuery,
    private relationshipService: RelationshipService,
    private departmentQuery: DepartmentQuery,
    private oaFollowersService: OAFollowersService,
    private oaFollowersQuery: OAFollowersQuery,
    private oaConnectionService: OAConnectionService,
  ) { }

  async getCallLogs(doMapping = false) {
    this.callLogStore.setLoading(true);
    try {
      const { filter,paging } = this.callLogQuery.Ui;

      const res = await this.callLogApi.getCallLogs({filter, paging});
      let callLogs: CallLog[] = res.call_logs;
      let contactIds = callLogs.map(calllog => calllog.contact_id);
      let userIds = callLogs.map(calllog => calllog.user_id);

      let calleeList = callLogs.map(calllog => calllog.callee.replace(/\s/g, ''));
      let callerList = callLogs.map(calllog => calllog.caller.replace(/\s/g, ''));

      if(doMapping) {

        const unique = (value, index, self) => {
          return self.indexOf(value) === index
        }
        const uniqueUserIds = userIds.filter(unique);
        const uniqueIds =  calleeList.concat(callerList).filter(uni => uni.length > 10 && uni.length <=20).filter(unique);

        let getAccountUsersFilter: AuthorizationAPI.GetAccountUserFilter = { user_ids: uniqueUserIds };
        await this.getAccountUsersByCallLogs(getAccountUsersFilter);

        let getContactsFilter: ContactAPI.GetContactsRequest = {
          filter: {ids: contactIds}
        };
        await this.getContactsByCallLogs(getContactsFilter);

        let getFollowOAFilter: ZaloAPI.FilterFollowersOA = {
          zl_user_ids: uniqueIds
        };

        await this.oaConnectionService.getListShopOA();

        await this.getFollowerOACallLogs(getFollowOAFilter);

        const relationships = this.relationshipQuery.getAll();
        const extensions = this.extensionQuery.getAll();
        const contacts = this.contactQuery.getAll();
        const hotlines = this.hotlineQuery.getAll();
        const departments = this.departmentQuery.getAll();
        const followersOA = this.oaFollowersQuery.getAll();

        callLogs = res.call_logs
          .map(callLog => this.callLogMap(callLog,relationships, extensions, hotlines, contacts, departments, followersOA));
        this.contactService.setFilter({});
      }
      this.setPaging(res.paging);
      this.callLogStore.set(callLogs)

    } catch(e) {
      debug.error('ERROR in getting CallLogs', e);
    }
    this.callLogStore.setLoading(false);
  }

  async createCallLog(request: CallLogAPI.CreateCallLogRequest) {
    return await this.callLogApi.createCallLog(request);
  }

  async getContactsByCallLogs(contactsFilter: ContactAPI.GetContactsRequest){
    this.contactService.setFilter(contactsFilter);
    await this.contactService.getContacts();
  }

  async getFollowerOACallLogs(filter: ZaloAPI.FilterFollowersOA){
    this.oaFollowersService.setFilter(filter);
    this.oaFollowersService.setPaging({limit: 100, after: '.'})
    await this.oaFollowersService.getListFollowers(false);
  }

  async getAccountUsersByCallLogs(filter: AuthorizationAPI.GetAccountUserFilter) {
    this.relationshipService.setFilter(filter);
    this.relationshipService.setPaging({
      limit: 100,
      after: '.',
    });
    await this.relationshipService.getRelationships();
  }

  setFilter(filter: CallLogAPI.GetCallLogsFilter) {
    const ui = this.callLogQuery.Ui;
      this.callLogStore.update({
      ui: {
        ...ui,
        filter
      }
    });
  }

  setPaging(paging: CursorPaging) {
    const ui = this.callLogQuery.Ui;
    this.callLogStore.update({
      ui: {
        ...ui,
        paging
      }
    });
  }

  setCurrentPage(currentPage: number) {
    const ui = this.callLogQuery.Ui;
    this.callLogStore.update({
      ui: {
        ...ui,
        currentPage: currentPage
      }
    });
  }

  // Gán relationship(Nhân viên), contact (Khách hàng), hotline (Tổng đài), From(người gọi), To (Người nghe) cho mỗi Call Log (Cuộc gọi).
  callLogsMap(callLogs: CallLog[]): CallLog[] {
    const relationships = this.relationshipQuery.getAll();
    const extensions = this.extensionQuery.getAll();
    const contacts = this.contactQuery.getAll();
    const hotlines = this.hotlineQuery.getAll();
    return callLogs.map(callLog => this.callLogMap(callLog, relationships, extensions, hotlines, contacts))
  }

  callLogMap(
    callLog: CallLog, 
    relationships?: Relationship[], 
    extensions?: Extension[], 
    hotlines?: Hotline[], 
    contacts?: Contact[], 
    departments?: Department[],
    followersOA?: Followers[], 
  ): CallLog {
    let mappingCallLog = {...callLog}

    //Xac dinh Hotline(tổng đài) cua callLog
    let extension = extensions.find(ext => ext.id == mappingCallLog.extension_id);
    let hotline = hotlines.find(hotl => hotl.id == callLog?.hotline_id);
    mappingCallLog.hotline = hotline;
    //Xac dinh may nhanh cua call log
    mappingCallLog.extension_number = extension?.extension_number;

    // Xác định, map relationship, contact ngừoi thực hiện cuộc gọi
    let callerExtension = extensions.find(ext => mappingCallLog.caller == ext.extension_number);
    if(callerExtension){
      let callerRelationship = relationships.find(rel => callerExtension.user_id == rel.user_id);
      mappingCallLog.callerRelationship = callerRelationship;
    }
    else{
      let contact: any = {};
      contact.phone = mappingCallLog.caller;
      let callerContact = contacts.find(cont => mappingCallLog.caller == cont.phone) || contact;
      mappingCallLog.callerContact = callerContact;
    }

    //Xác định, map relationship, contact của ngừoi nhận cuộc gọi
    let calleeExtension = extensions.find(ext => mappingCallLog.callee == ext.extension_number);
    if(calleeExtension){
      let calleeRelationship = relationships.find(rel => calleeExtension.user_id == rel.user_id);
      mappingCallLog.calleeRelationship = calleeRelationship;
    }
    else{
      let contact: any = {};
      contact.phone = mappingCallLog.callee;
      let calleeContact = contacts.find(cont => mappingCallLog.callee == cont.phone) || contact;
      mappingCallLog.calleeContact = calleeContact;
    }

    //Xác định người gọi đến của call log
    let from = mappingCallLog.callerRelationship ? mappingCallLog.callerRelationship : mappingCallLog.callerContact;
    mappingCallLog.from = from;

    //Xác định người nghe của call log
    let to = mappingCallLog.calleeRelationship ? mappingCallLog.calleeRelationship : mappingCallLog.calleeContact
    mappingCallLog.to = to;

    mappingCallLog.reasson_fail_dislay = this.reasonFailcode(mappingCallLog?.call_targets[0]?.fail_code)

    mappingCallLog.department = departments?.find(d => d.id == mappingCallLog.department_id)

    mappingCallLog.zl_user_name_callee = followersOA?.find(follower => follower.zl_user_id == mappingCallLog.callee)?.full_name

    mappingCallLog.zl_user_name_caller = followersOA?.find(follower => follower.zl_user_id == mappingCallLog.caller)?.full_name

    mappingCallLog.ended_reason_display = END_REASON[mappingCallLog?.ended_reason]

    return mappingCallLog;
  }

  reasonFailcode(code) {
    switch (code) {
      case 480:
      case 487:
        return 'Không nhấc máy/ thuê bao/ sai số/ Hotline bị khóa'
      case 486:
        return 'Bận/ Từ chối';
      case 401:
        return 'Cuộc gọi bị chặn';
      default:
        return null;
    }
  }

  setCallLogs(callLogs: CallLog[]) {
    this.callLogStore.set(callLogs);
  }

  changeHeaderTab(tab: CallLogTab) {
    const ui = this.callLogQuery.Ui;
    this.callLogStore.update({
      ui: {
        ...ui,
        tab
      }
    })
  }

  setLoading(loading: boolean) {
    this.callLogStore.setLoading(loading);
  }
}
